import { Alert, Button, Input, Radio, Space } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { DetailHeader as ZhDetailHeader, Dialog as ZhDialog } from "zhihao-ui-react";

import request from "@/api";
import DetailJson, { type DetailJsonBlock } from "@/views/components/detailJson";
import { useUserStore } from "@/views/stores";
import type { FundingCluesForm, FundingCluesVo } from "@zhihaoscm/entity";
import { FUNDING_CLUES_STATE, FUNDING_CLUES_TYPE, REGISTER_SOURCE } from "@zhihaoscm/meta";

export interface FundingCluesDetailPageProps {
    loadDetail?: (id: number | string) => Promise<FundingCluesVo>;
    handleClue?: (id: number | string, payload: FundingCluesForm) => Promise<unknown>;
    permissions?: string[];
}

/** 沿用 Vue FundingCluesService.getVo 的资金线索详情接口。 */
export async function loadFundingClueDetail(id: number | string): Promise<FundingCluesVo> {
    const response = await request.service.get<FundingCluesVo>(`/admin/funding/clues/vo/${id}`);
    return response.data;
}

/** 沿用 Vue FundingCluesService.handle 的资金线索处理接口。 */
export async function handleFundingClueDetail(id: number | string, payload: FundingCluesForm): Promise<unknown> {
    const response = await request.service.put(`/admin/funding/clues/${id}`, payload);
    return response.data;
}

/** 复用 Vue 详情页中的反馈账号拼接规则。 */
function getCustomerLabel(detail: FundingCluesVo): string {
    const customer = detail.fundingClues.customerEnterprise;
    return (
        [customer?.institutionName || customer?.realName, customer?.mobile, customer?.code, customer?.appTypeName]
            .filter(Boolean)
            .join(" | ") || "--"
    );
}

/** 沿用 Vue 资金金额的无小数位格式化规则。 */
function formatAmount(value: number | undefined): string {
    return (Number(value) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

/** 保留 Vue dateTimeFormat 在详情头部和处理记录中的分钟级展示。 */
function formatDateTime(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : "-";
}

/** 用户申请资金详情页，保留 Vue 基本信息、处理记录及处理弹窗。 */
export function FundingCluesDetailPage({
    loadDetail = loadFundingClueDetail,
    handleClue = handleFundingClueDetail,
    permissions
}: FundingCluesDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 沿用 Vue 全局封禁态，资金线索详情处理入口保留但禁止触发。
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    const id = new URLSearchParams(location.search).get("id") || "";
    const [detail, setDetail] = useState<FundingCluesVo | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [handleVisible, setHandleVisible] = useState(false);
    const [handleState, setHandleState] = useState(2);
    const [remark, setRemark] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const canManage = currentPermissions.includes("ROLE_FUNDS_W");
    const detailColumns: DetailJsonBlock[] = [
        {
            sectionTitle: "基本信息",
            list: [
                { label: "反馈账号", value: () => (detail ? getCustomerLabel(detail) : "-") },
                {
                    label: "线索来源",
                    value: () => REGISTER_SOURCE.getIntroByCode(detail?.fundingClues.cluesSource) || "-"
                },
                {
                    label: "资金需求类型",
                    value: () => FUNDING_CLUES_TYPE.getIntroByCode(detail?.fundingClues.type) || "-"
                },
                { label: "预计资金需求(元)", value: () => formatAmount(detail?.fundingClues.fundingAmount) },
                { label: "姓名", value: () => detail?.fundingClues.name || "-" },
                { label: "电话", value: () => detail?.fundingClues.mobile || "-" },
                { label: "公司名称", value: () => detail?.fundingClues.companyName || "-" },
                { label: "职位", value: () => detail?.fundingClues.position || "-" },
                { label: "申请时间", value: () => formatDateTime(detail?.fundingClues.createdTime) }
            ]
        },
        {
            sectionTitle: "处理记录",
            show: Number(detail?.fundingClues.state) !== FUNDING_CLUES_STATE.PENDING_PROCESSING.code,
            list: [{ rowSlot: "record", span: 4 }]
        }
    ];

    /** 打开 Vue 处理弹窗时重置处理状态和备注。 */
    const openHandleDialog = () => {
        setHandleState(2);
        setRemark("");
        setErrorMessage(null);
        setHandleVisible(true);
    };

    /** 提交资金线索处理 payload，成功后重新获取详情。 */
    const submitHandle = async () => {
        if (!id || !remark.trim()) {
            setErrorMessage("请输入备注");
            return;
        }
        setSubmitting(true);
        try {
            await handleClue(id, { state: handleState, remark: remark.trim() });
            setHandleVisible(false);
            setErrorMessage(null);
            setDetail(await loadDetail(id));
        } catch {
            setErrorMessage("资金申请处理失败");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (!id) {
            setErrorMessage("缺少资金申请编号");
            return;
        }
        let disposed = false;
        void loadDetail(id)
            .then(result => {
                if (!disposed) {
                    setDetail(result);
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("用户申请资金详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    return (
        <section
            aria-label="funding-clues-detail-page"
            className="detail-json-page detail-page-transparent admin-detail-page"
        >
            <div className="section-card admin-detail-header-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "序号", value: detail?.fundingClues.id || id || "-" }]}
                    rightInfos={[{ label: "创建时间", value: formatDateTime(detail?.fundingClues.createdTime) }]}
                    stateDesc={FUNDING_CLUES_STATE.getIntroByCode(detail?.fundingClues.state) || "-"}
                    title="用户申请资金详情"
                />
            </div>
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            {detail ? (
                <DetailJson
                    columns={detailColumns}
                    slots={{
                        record: () => (
                            <div className="flex w-full flex-col gap-4">
                                {(detail.fundingClues.modifyRecord || []).map((record, index) => (
                                    <div key={`${record.updatedTime}-${index}`} className="flex gap-4">
                                        <div className="text-black/65">{index + 1}.</div>
                                        <div className="flex flex-col gap-4">
                                            <div className="text-black/65">
                                                {formatDateTime(record.updatedTime)}，处理人：{record.handlerName}
                                            </div>
                                            <div className="text-black/65">备注：{record.remark}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    }}
                />
            ) : null}
            <Space className="operation-fixed-btn" size={0}>
                {detail && Number(detail.fundingClues.state) !== FUNDING_CLUES_STATE.PROCESSED.code && canManage ? (
                    <Button aria-label="处理" disabled={banButton} onClick={openHandleDialog} type="primary">
                        处理
                    </Button>
                ) : null}
                <Button onClick={() => navigate(-1)}>返回</Button>
            </Space>
            <ZhDialog
                alignCenter
                modalClass="user-operation-funding-dialog"
                open={handleVisible}
                title="处理"
                width={544}
                onCancel={() => setHandleVisible(false)}
                footer={
                    <>
                        <Button onClick={() => setHandleVisible(false)}>取消</Button>
                        <Button
                            aria-label="确认处理"
                            loading={submitting}
                            onClick={() => void submitHandle()}
                            type="primary"
                        >
                            确认
                        </Button>
                    </>
                }
            >
                <div className="user-operation-action-dialog-form">
                    <div className="user-operation-action-dialog-form-item user-operation-action-dialog-form-item--funding">
                        <span className="user-operation-action-dialog-form-label">是否处理</span>
                        <span className="user-operation-action-dialog-form-control">
                            <Radio.Group
                                aria-label="是否处理"
                                className="user-operation-action-dialog-radio-group"
                                value={handleState}
                                onChange={event => setHandleState(event.target.value)}
                            >
                                <Radio value={2}>处理中</Radio>
                                <Radio value={3}>已处理</Radio>
                            </Radio.Group>
                        </span>
                    </div>
                    <label className="user-operation-action-dialog-form-item user-operation-action-dialog-form-item--funding">
                        <span className="user-operation-action-dialog-form-label">
                            <span className="user-operation-action-dialog-required">*</span>
                            备注
                        </span>
                        <span className="user-operation-action-dialog-form-control">
                            <Input.TextArea
                                aria-label="备注"
                                className="user-operation-action-dialog-textarea"
                                maxLength={200}
                                placeholder="请输入备注"
                                rows={3}
                                showCount
                                value={remark}
                                onChange={event => setRemark(event.target.value)}
                            />
                        </span>
                    </label>
                </div>
            </ZhDialog>
        </section>
    );
}

export default FundingCluesDetailPage;
